import "./PaymentManager.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:5000";

export default function PaymentManager() {
    const navigate = useNavigate();

    const [tenants, setTenants] = useState([]);
    const [apartments, setApartments] = useState([]);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [form, setForm] = useState({
        tenant_id: "",
        month: "",
        luce: "",
        acqua: "",
        total: 0
    });

    const mesi = [
        "Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
        "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"
    ];

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => {
            setToast("");
        }, 2500);
    };

    const getAuthHeaders = () => {
        const token = localStorage.getItem("access_token");
        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };
    };

    const handleUnauthorized = () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("username");
        localStorage.removeItem("role");
        navigate("/login");
    };

    useEffect(() => {
        fetchTenants();
        fetchApartments();
    }, []);

    const fetchTenants = async () => {
        setError("");

        try {
            const r = await fetch(`${API_URL}/tenants/?status=active`, {
                headers: getAuthHeaders(),
            });

            if (r.status === 401 || r.status === 422) {
                handleUnauthorized();
                return;
            }

            const data = await r.json();

            if (!r.ok) {
                setError(data?.error || "Gabim gjatë ngarkimit të qiramarrësve.");
                return;
            }

            const activeTenants = (Array.isArray(data) ? data : [])
                .filter((t) => t.is_active !== false)
                .sort((a, b) => String(a.apartment_name || "").localeCompare(
                    String(b.apartment_name || ""),
                    undefined,
                    { numeric: true, sensitivity: "base" }
                ));
            setTenants(activeTenants);
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    const fetchApartments = async () => {
        setError("");

        try {
            const r = await fetch(`${API_URL}/apartments/`, {
                headers: getAuthHeaders(),
            });

            if (r.status === 401 || r.status === 422) {
                handleUnauthorized();
                return;
            }

            const data = await r.json();

            if (!r.ok) {
                setError(data?.error || "Gabim gjatë ngarkimit të apartamenteve.");
                return;
            }

            setApartments(data);
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    const getApartmentName = (apartmentId) => {
        const apt = apartments.find((a) => a.id === apartmentId);
        return apt ? apt.name : "";
    };

    const formatLek = (value) =>
        Number(value || 0).toLocaleString("it-IT") + " Lek";

    const savePayment = async () => {
        setError("");

        if (!form.tenant_id || !form.month) {
            setError("Zgjidh qiramarrësin dhe muajin.");
            return;
        }

        try {
            const response = await fetch(`${API_URL}/payments/`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    ...form,
                    tenant_id: Number(form.tenant_id),
                    luce: Number(form.luce || 0),
                    acqua: Number(form.acqua || 0),
                })
            });

            if (response.status === 401 || response.status === 422) {
                handleUnauthorized();
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                setError(data?.error || "Gabim gjatë regjistrimit të pagesës.");
                return;
            }

            showToast("Pagesa u regjistrua me sukses.");
            setForm({
                tenant_id: "",
                month: "",
                luce: "",
                acqua: "",
                total: 0
            });
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    return (
        <div className="payment-manager-container">
            <h2 className="payment-manager-title">💳 Regjistro Pagesë</h2>

            {toast && (
                <div
                    style={{
                        position: "fixed",
                        top: "20px",
                        right: "20px",
                        background: "#16a34a",
                        color: "#fff",
                        padding: "12px 18px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                        fontWeight: "600",
                        zIndex: 9999,
                        minWidth: "240px",
                    }}
                >
                    ✅ {toast}
                </div>
            )}

            {error && (
                <div style={{ marginBottom: "15px", color: "#b00020", fontWeight: "600" }}>
                    {error}
                </div>
            )}

            <div className="payment-form">
                <label>Qiramarrësi</label>
                <select
                    value={form.tenant_id}
                    onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                >
                    <option value="">Zgjidh qiramarrësin</option>
                    {tenants.map((t) => {
                        const apt = getApartmentName(t.apartment_id);
                        return (
                            <option key={t.id} value={t.id}>
                                {t.name} — {apt}
                            </option>
                        );
                    })}
                </select>

                <label>Muaji</label>
                <select
                    value={form.month}
                    onChange={(e) => setForm({ ...form, month: e.target.value })}
                >
                    <option value="">Zgjidh muajin</option>
                    {mesi.map((m) => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>

                <label>Drita (Lek)</label>
                <input
                    type="number"
                    placeholder="Shkruaj shumën për dritat"
                    value={form.luce}
                    onChange={(e) =>
                        setForm({
                            ...form,
                            luce: e.target.value,
                            total: Number(e.target.value || 0) + Number(form.acqua || 0)
                        })
                    }
                />

                <label>Ujë (Lek)</label>
                <input
                    type="number"
                    placeholder="Shkruaj shumën për ujin"
                    value={form.acqua}
                    onChange={(e) =>
                        setForm({
                            ...form,
                            acqua: e.target.value,
                            total: Number(e.target.value || 0) + Number(form.luce || 0)
                        })
                    }
                />

                <div className="payment-total-box">
                    <span>Totali:</span>
                    <b>{formatLek(form.total)}</b>
                </div>

                <button className="payment-btn" onClick={savePayment}>
                    💾 Ruaj Pagesën
                </button>
            </div>
        </div>
    );
}
